import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIsMounted } from '../utils/ReactUtils'
import {MaterialReactTable, useMaterialReactTable} from 'material-react-table'
import { Button, Grid, Paper} from '@mui/material'
import axios from 'axios'
import { Link } from 'react-router-dom';
import { getRequestConfig } from '../auth/auth.js'
import { ApiGateway } from '../config/config.js';
import { mkConfig, generateCsv, download } from 'export-to-csv'
import { Box } from '@mui/system'
import { FileDownload } from '@mui/icons-material'

export default function LisComplaintTrends(props = {searchFilter: undefined, rowsPerPage: 100, pageIndex: 0, omitColumns: [], minHeight: 300 }) {
  const isMounted = useIsMounted()

  const { omitColumns = [], searchFilter, startLoading, stopLoading } = props

  const [dataRows, setDataRows] = useState([])

  const columns = useMemo(() => ([
    {
      accessorKey: 'trendNumber',
      header: 'Trend',
      size: 100,
      editable: false,
      filterVariant: 'text',
      grow: false,
      Cell: ({row}) => {
        const complaintTrendId = row.original.id
        return (
          <Link to={ `/complaint-trends/${complaintTrendId}` }>
            {row.original.trendNumber} - {row.original.plantCode}
          </Link>
        )
      }
    },
    {
      accessorKey: 'trendDate',
      header: 'Date',
      editable: false,
      filterVariant: 'date',
      filterFn: (row, key, val) => {
        if(isNaN(val.year())) return true
        if(typeof val.year !== 'function') return true
        const trendDate = new Date(row.original.trendDate)
        return trendDate.getFullYear() === val.year()
        && trendDate.getMonth() === val.month()
        && trendDate.getDate() === val.date()
      },
      sortingFn: 'datetime',
      size: 85,
      grow: false,
      Cell: ({ cell, row }) => {
        const d = row.original.trendDate
        if (d && d !== null) {
          return `${new Date(d).toLocaleDateString('en-US', {timeZone: 'UTC'})}`
        }
      }
    },
    {
      accessorKey: 'trendIssue',
      header: 'Trend Issue(s)',
      size: 120,
      grow: true,
      editable: false,
      filterVariant: 'text',
      Cell: ({ cell, row }) => {
        return `${Array.from(new Set((cell.getValue() ?? '').split('~'))).join('; ')}`
      }
    },
    {
      accessorKey: 'productNames',
      header: 'Product Name(s)',
      size: 140,
      grow: true,
      editable: false,
      filterVariant: 'text',
      Cell: ({ cell, row }) => {
        return `${Array.from(new Set((cell.getValue() ?? '').split(','))).join('; ')}`
      }
    },
    {
      accessorKey: 'manufacturerName',
      accessorFn: (row) => row?.Manufacturer?.manufacturerName,
      header: 'Facility',
      grow: true,
      editable: false,
      filterVariant: 'text',
      Cell: ({row}) => {
        if ([undefined, null].includes(row.original.Manufacturer)) return
        return (
        <Link to={"/manufacturer/edit/" + row.original.Manufacturer.uuid + '/details'}>
          {row.original.Manufacturer.manufacturerName}
        </Link>
      )}
    },
    {
      accessorKey: 'ownershipName',
      accessorFn: (row) => row?.Manufacturer?.Ownership?.ownershipName,
      header: 'Ownership',
      size: 140,
      grow: true,
      editable: false,
      filterVariant: 'text',
      Cell: ({ row }) => {
        if ([undefined, null].includes(row.original.Manufacturer)) return
        const ownership = row.original.Manufacturer.Ownership
        return (
        <Link to={"/ownership/edit/" + ownership?.uuid + '/details'}>
          {ownership?.ownershipName}
        </Link>
      )}
    },
    {
      accessorKey: 'manager',
      accessorFn: (row) => [row?.Manager?.firstName,row?.Manager?.lastName,row?.Manager?.email,row?.Manager?.koId].join(' '),
      header: 'Manager',
      editable: false,
      grow: true,
      maxSize: 150,
      filterVariant: 'text',
      Cell: ({row}) => (
          <span style={{textTransform: 'capitalize'}}>{([row.original.Manager?.firstName ?? '', row.original.Manager?.lastName ?? ''].join(' ')).trim().toLowerCase()}</span>
          )
    }
  ]), [])

  const listColumns = useMemo(() => {
    return columns.filter(c => !omitColumns.includes(c.header.toLowerCase()))
  }, [columns, omitColumns])

  const handleExportRows = (rows) => {
    const rowData = rows.map(row => {
      const r = row.original
      const data = listColumns.reduce((p,c) => {
        let d = undefined
        const key = c.accessorKey
        switch (key) {
          case 'trendNumber':
            d = `${r.trendNumber} - ${r.plantCode}`
            break
          case 'trendDate':
            d = `${new Date(r.trendDate).toLocaleDateString('en-US', {timeZone: 'UTC'})}`
            break
          case 'trendIssue':
            d = `${Array.from(new Set((r.trendIssue ?? '').split('~'))).join('; ')}`
            break
          case 'productNames':
            d = `${Array.from(new Set((r.productNames ?? '').split(','))).join('; ')}`
            break
          case 'manufacturerName':
            d = r.Manufacturer?.manufacturerName
            break
          case 'ownershipName':
            d = r.Manufacturer?.Ownership?.ownershipName
            break
          case 'manager':
            d = ([r.Manager?.firstName ?? '', r.Manager?.lastName ?? ''].join(' ')).trim()
            break
          default:
            if(c.accessorFn) {
              d = c.accessorFn(r)
            }
        }
        if (d) return { ...p, [key]: d }
        return p
      }, {})
      return data
    })
    const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    columnHeaders: listColumns.map(c => { return { key: c.accessorKey, displayLabel: c.header}}),
    fileExtension: 'csv',
    filename: 'trends'
  })
    const csv = generateCsv(csvConfig)(rowData)
    download(csvConfig)(csv)
  }

  const getComplaintTrendsList = useCallback(async () => {
    if (startLoading !== undefined) startLoading()

    try {
      const requestConfig = await getRequestConfig()
      const basePath = `${ApiGateway.complaintTrends}/search`
      const search = searchFilter ?? {}
      const response = await axios.post(basePath, search, requestConfig)
      const results = response?.data?.results
      setDataRows(Array.isArray(results) ? results : [])
    } catch (error) {
      console.error(error)
      setDataRows([])
    } finally {
      if (stopLoading !== undefined) stopLoading()
    }
  }, [searchFilter, startLoading, stopLoading])

  

  useEffect(() => {
    if (isMounted()) {
      getComplaintTrendsList()
    }
  }, [getComplaintTrendsList, isMounted])

  const table = useMaterialReactTable({
    layoutMode: 'grid',
    columnFilterModeOptions: [
      'fuzzy',
      'greaterThan',
      'lessThan'
    ],
    columns: listColumns,
    data: dataRows,
    enableColumnActions: false,
    enableColumnFilters: props.showFilters,
    initialState: {
      showColumnFilters: true,
      pagination: {
        pageIndex: props.pageIndex ?? 0,
        pageSize: props.rowsPerPage ?? 100
      }
    },
    enablePagination: true,
    paginationDisplayMode: 'default',
    enableSorting: true,
    enableEditing: false,
    muiTableContainerProps: {
      sx: { minHeight: props.minHeight ?? 300 },

    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: '.8rem !important',
        padding: '4px !important'
      }
    },
    enableTopToolbar: false,
    enableBottomToolbar: true,
    renderBottomToolbarCustomActions: ({table}) => {
      return <Box sx={{display: 'flex'}}>
        <Button onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)} startIcon={<FileDownload />}>
          Export to CSV
        </Button>
      </Box>
    }
  });


  return (
    <div className='App'>
      <Paper sx={{ backgroundColor: 'transparent', p: '0 0 1em 0', border: 'none', boxShadow: 'none', display: 'flex', flexDirection: 'column', flexWrap: 'wrap', width: '100%'}}>
        <Grid container rowSpacing={2} >
          <Grid item xs={true} md={true}>            
            <MaterialReactTable table={table} />
          </Grid>
        </Grid>
      </Paper>
    </div>
  )

}